"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { OurTeamMemberProps } from "@/types/our-team/our-team.types";
import { contactData } from "@/data/contact-info.data";
import { FaGlobe } from "react-icons/fa6";
import { useDict } from "@/components/providers/LocaleProvider";

interface OurTeamClientProps {
  teamMembers: OurTeamMemberProps[];
}

const OurTeamClient: React.FC<OurTeamClientProps> = ({ teamMembers }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ref = containerRef.current;
    if (!ref) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1,
      }
    );

    observer.observe(ref);

    return () => {
      observer.unobserve(ref);
    };
  }, []);

  useEffect(() => {
    // Only run auto-reveal on large devices when not hovering and component is visible
    const isLargeScreen = window.matchMedia("(min-width: 1024px)").matches;

    if (isLargeScreen && teamMembers.length > 0 && !isHovering && isVisible) {
      const interval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % teamMembers.length);
      }, 3000); // Change every 3 seconds

      return () => clearInterval(interval);
    }
  }, [teamMembers.length, isHovering, isVisible]);

  const t = useDict();
  const designationsDict = t.home.team.designations;
  const designationFor = (member: OurTeamMemberProps) =>
    member.designationKey && designationsDict[member.designationKey]
      ? designationsDict[member.designationKey]
      : member.designation;

  const handleMouseEnter = (index: number) => {
    setIsHovering(true);
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setHoveredIndex(null);
  };

  return (
    <div
      ref={containerRef}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 "
    >
      {teamMembers.map((member, index) => (
        <Card
          key={member.id}
          member={member}
          designation={designationFor(member)}
          index={index}
          isActive={
            hoveredIndex !== null
              ? index === hoveredIndex
              : index === activeIndex
          }
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
        />
      ))}
    </div>
  );
};

export default OurTeamClient;

// Card component with staggered height and auto-reveal
const Card: React.FC<{
  member: OurTeamMemberProps;
  designation: string;
  index: number;
  isActive: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}> = ({ member, designation, isActive, onMouseEnter, onMouseLeave }) => {
  // Stagger logic: items at index 1 and 3 will be lower
  // const staggerClass = index % 2 === 0 ? "md:mt-0 md:mb-8" : "md:mt-8 md:mb-0";

  const staggerClass = "";

  return (
    <div
      className={`h-[450px] group md:h-[500px]   duration-300 lg:h-auto bg-background rounded-2xl ${staggerClass}`}
      style={{
        backgroundImage: "linear-gradient(90deg, #5442b9, #1c212b, #5442b9)",
        animation: "waveSlide 5s linear infinite",
        backgroundSize: "200% auto",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="h-[calc(100%-5px)] w-full hover:-translate-y-4 duration-300 relative rounded-xl overflow-hidden shadow-lg bg-dark-gray">
        {/* Image */}
        <Image
          src={member.image}
          alt={member.name}
          height={500}
          width={500}
          className="w-full h-full group-hover:scale-110 duration-500 object-cover"
        />
        {/* Overlay */}
        <div
          className={`
            absolute self-end inset-0 bg-black/45 px-4 py-5 flex flex-col justify-end h-fit
            transition-transform duration-800 ease-in-out
            translate-y-0
            lg:group-hover:translate-y-0
            ${isActive ? "lg:translate-y-0" : "lg:translate-y-full"}
          `}
        >
          {/* Title */}
          <div
            className={`
              opacity-100 transition-opacity duration-500 delay-100
              lg:group-hover:opacity-100
              ${isActive ? "lg:opacity-100" : "lg:opacity-0"}
            `}
          >
            <span className="text-light-text text-xs block mb-1">
              {designation}
            </span>
            <p className="card-title">{member.name}</p>
          </div>
          {/* Description */}
          <p
            className={`
              text-light-text text-xs mt-2 transition-opacity duration-500 delay-200
              opacity-100
              lg:group-hover:opacity-100
              ${isActive ? "lg:opacity-100" : "lg:opacity-0"}
            `}
          />
          {/* Social Links */}
          {(member.email || member.linkedin || member.whatsapp) && (
            <div
              className={`
                flex gap-3 mt-4 text-xs transition-opacity duration-500 delay-300
                opacity-100
                lg:group-hover:opacity-100
                ${isActive ? "lg:opacity-100" : "lg:opacity-0"}
              `}
            >
              {member.email && (
                <Link
                  href={`mailto:${member.email}`}
                  area-label={"Email"}
                  className="text-light-text   hover:text-dark-gray rounded-full p-1 transition-all duration-300 ease-in-out transform hover:scale-125 flex justify-center items-center size-10 "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    src={contactData[3].src}
                    alt="Email Icon"
                    width={20}
                    height={20}
                  />
                </Link>
              )}
              {member.linkedin && (
                <Link
                  href={member.linkedin}
                  className="text-light-text   hover:text-dark-gray rounded-full p-1 transition-all duration-300 ease-in-out transform hover:scale-125 flex justify-center items-center size-10"
                  target="_blank"
                  rel="noopener noreferrer"
                  area-label={"Linkedin"}
                >
                  <Image
                    src={contactData[1].src}
                    alt="LinkedIn Icon"
                    width={28}
                    height={28}
                  />
                </Link>
              )}
              {member.whatsapp && (
                <Link
                  href={`https://wa.me/${member.whatsapp.replace(
                    /[^0-9]/g,
                    ""
                  )}`}
                  className="text-light-text   hover:text-dark-gray rounded-full p-1 transition-all duration-300 ease-in-out transform hover:scale-125 size-10"
                  target="_blank"
                  rel="noopener noreferrer"
                  area-label={"Whatsapp"}
                >
                  <Image
                    src={contactData[0].src}
                    alt="Whatsapp Icon"
                    width={26}
                    height={26}
                  />
                </Link>
              )}
              {member?.portfolio && (
                <Link
                  href={member?.portfolio}
                  className="text-light-text  rounded-full transition-all duration-300 ease-in-out transform hover:scale-125 flex justify-center items-center"
                  target="_blank"
                  rel="noopener noreferrer"
                  area-label={"Portfolio"}
                >
                  <FaGlobe  size={23}/>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
